import firebaseApp from "../firebase/credenciales";
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const db = getFirestore(firebaseApp);

/**
 * Guarda un mensaje en la colección ia_chats/{dni}/agentId
 * @param {string} dni - DNI del usuario
 * @param {string} agentId - ID del agente
 * @param {Object} data - Datos del mensaje: { text, isUser }
 * @returns {Promise<Object>} Resultado de la operación
 */
export default async function sendNotification(dni, agentId, data) {
  try {
    const collectionRef = collection(db, `ai_chats/${dni}/${agentId}`);
    const docData = {
      isUser: false,
      read: false,
      text: data.text || "",
      timestamp: serverTimestamp(),
    };
    const docRef = await addDoc(collectionRef, docData);
    return {
      success: true,
      id: docRef.id,
      ref: docRef
    };
  } catch (error) {
    console.error("Error en sendNotification:", error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Envía notificaciones en lotes de máximo 600 por segundo, con reintentos para fallidos.
 * @param {Array} notifications - Array de objetos { dni, agentId, data }
 * @param {number} batchSize - Tamaño del lote (máximo 600)
 * @param {number} pauseMs - Pausa entre lotes en milisegundos (por defecto 1100ms)
 * @param {number} maxRetries - Número máximo de reintentos para fallidos
 * @returns {Promise<Object>} Resultados del envío
 */
export async function sendNotificationBatch(
  notifications,
  batchSize = 600,
  pauseMs = 1100,
  maxRetries = 2
) {
  const results = {
    total: notifications.length,
    success: 0,
    failed: 0,
    errors: []
  };

  let pendientes = [...notifications];
  let intentos = 0;

  while (pendientes.length > 0 && intentos <= maxRetries) {
    for (let i = 0; i < pendientes.length; i += batchSize) {
      const batch = pendientes.slice(i, i + batchSize);

      const batchResults = await Promise.all(
        batch.map(async (notif) => {
          try {
            const res = await sendNotification(notif.dni, notif.agentId, notif.data);
            if (res.success) {
              results.success++;
              return { ...notif, success: true };
            } else {
              results.failed++;
              results.errors.push({ ...notif, error: res.error });
              return { ...notif, success: false, error: res.error };
            }
          } catch (error) {
            results.failed++;
            results.errors.push({ ...notif, error: error.message });
            return { ...notif, success: false, error: error.message };
          }
        })
      );

      // Si quedan más lotes, esperar antes de continuar
      if (i + batchSize < pendientes.length) {
        await new Promise((resolve) => setTimeout(resolve, pauseMs));
      }

      // Filtrar los fallidos para reintentar
      pendientes = batchResults.filter(r => !r.success);
    }
    intentos++;
  }

  return results;
}